import { useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/common";
import { CommissionReport } from "@/components/CommissionReport";
import { useFetch } from "@/hooks/useFetch";
import { money, fmtDate } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

const COLS = [["jobs", "Jobs", false], ["revenue", "Revenue", true], ["company_billed", "Billed to companies", true], ["deposits", "Deposits", true], ["driver_extras", "Driver extras", true], ["crew_pay", "Crew pay", true], ["profit", "Profit (revenue − crew pay)", true]];

export default function ReportsPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useFetch("/reports/monthly", { year });
  const rows = (data || []).filter((r) => r.jobs || r.cancelled);
  const total = (k) => rows.reduce((s, r) => s + r[k], 0);
  const fmt = (k, v, isMoney) => (isMoney ? money(v) : v);

  const exportCsv = () => {
    const lines = ["Month," + COLS.map(([, l]) => l).join(",")];
    rows.forEach((r) => lines.push([r.month, ...COLS.map(([k]) => r[k])].join(",")));
    lines.push(["TOTAL", ...COLS.map(([k]) => total(k).toFixed(2))].join(","));
    downloadCsv(`monthly-report-${year}.csv`, lines.join("\r\n"));
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Monthly revenue, deposits, driver extras, crew pay and profit, plus sales commission by person. Cancelled jobs are excluded.">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setYear(year - 1)} data-testid="report-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[70px] text-center font-mono text-lg font-bold" data-testid="report-year">{year}</span>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setYear(year + 1)} data-testid="report-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="report-export"><Download className="h-4 w-4" /> Export CSV</Button>
      </PageHeader>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["revenue", "Revenue", "text-[#0E1E38]"], ["deposits", "Deposits", "text-emerald-700"], ["crew_pay", "Crew pay", "text-amber-800"], ["profit", "Profit", total("profit") >= 0 ? "text-emerald-700" : "text-rose-700"]].map(([k, l, c]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`report-total-${k}`}><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l} {year}</div><div className={cn("mt-1 font-mono text-2xl font-bold", c)}>{money(total(k))}</div></div>
        ))}
      </div>
      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="report-table">
            <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"><th className="pb-2 pr-3">Month</th>{COLS.map(([k, l]) => <th key={k} className="pb-2 pr-3 text-right">{l}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-500" data-testid="report-empty">No jobs in {year}.</td></tr>}
              {rows.map((r) => (
                <tr key={r.month} className="border-t border-slate-100" data-testid={`report-row-${r.month}`}>
                  <td className="py-2.5 pr-3 font-medium">{fmtDate(`${r.month}-01`, "MMMM")}{r.cancelled ? <span className="ml-2 text-xs text-slate-400">{r.cancelled} cancelled</span> : null}</td>
                  {COLS.map(([k, , m]) => <td key={k} className={cn("py-2.5 pr-3 text-right font-mono", k === "profit" && (r[k] >= 0 ? "font-semibold text-emerald-700" : "font-semibold text-rose-700"))}>{fmt(k, r[k], m)}</td>)}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && <tfoot><tr className="border-t-2 border-[#0E1E38] font-semibold"><td className="py-2.5 pr-3">Total</td>{COLS.map(([k, , m]) => <td key={k} className="py-2.5 pr-3 text-right font-mono">{fmt(k, Math.round(total(k) * 100) / 100, m)}</td>)}</tr></tfoot>}
          </table>
        </div>
      </Panel>
      <div className="mt-6"><CommissionReport year={year} /></div>
    </div>
  );
}
